'use client';

import React, { useEffect, useState } from 'react';

import Image from 'next/image';

import { ResultType, getRandomValue, defaultFortune, fortuneData, checkList } from '@/app/utils/omikujiData';

const OmikujiContents = () => {
  const [result, setResult] = useState<ResultType>(defaultFortune);
  const [seasonsImage, setSeasonsImage] = useState<string>('/images/omikuji_top_torii.png');
  const [omikujiNumSwitching, setOmikujiNumSwitching] = useState(false);

  const kanjiDigits = ['零', '壱', '弐', '参', '肆', '伍', '陸', '漆', '捌', '玖'];

  // 季節ごとの画像を選定
  const getSeasonImage = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const date = now.getDate();

    switch (month) {
      case 3:
      case 4:
      case 5:
        return '/images/omikuji_top_spring.png';

      case 6:
      case 7:
      case 8:
        return '/images/omikuji_top_summer.png';

      case 9:
      case 10:
      case 11:
        return '/images/omikuji_top_autumn.png';

      case 12:
      case 2:
        return '/images/omikuji_top_winter.png';

      case 1:
        if (date < 8) {
          return '/images/omikuji_top_winter1.png';
        } else {
          return '/images/omikuji_top_winter.png';
        }

      default:
        return '/images/omikuji_top_torii.png';
    }
  };

  // 当月画像を取得
  useEffect(() => {
    setSeasonsImage(getSeasonImage);
  }, []);

  // 漢数字の文字列に桁毎の単位を追加
  const numToKanjiPlusDigit = (numbers: string) => {
    const nums = numbers.split('');
    const result = [];

    // 現状３桁のみ対応
    const digits = ['佰', '拾', ''];
    const offset = digits.length - nums.length;

    for (let i = 0; i < nums.length; i++) {
      const num = nums[i];

      if (num === '壱') {
        if (i === nums.length - 1) {
          result.push('壱');
        } else {
          result.push(digits[i + offset]);
        }
      } else if (num === '零') {
        result.push('');
      } else {
        result.push(`${num}${digits[i + offset]}`);
      }
    }

    return result.join('');
  };

  // 数値を漢数字へ変換
  const numToKanji = (num: number | undefined) => {
    const kanji = String(num).replace(/\d/g, (d) => kanjiDigits[parseInt(d)]);

    return numToKanjiPlusDigit(kanji);
  };

  const drawFortune = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    const fortunes = fortuneData;
    const randomIndex = getRandomValue(10000);

    let probability = 0;
    let sumCount: number = 0;

    fortunes.some((fortune) => {
      probability += fortune.probability;

      const { wish, business, health } = fortune.detail;

      if (randomIndex <= probability) {
        const wishNum = getRandomValue(wish.length);
        const businessNum = getRandomValue(business.length);
        const healthBum = getRandomValue(health.length);

        setResult({
          ...fortune,
          detail: {
            wish: wish[wishNum],
            business: business[businessNum],
            health: health[healthBum],
          },
          checkList: checkList[getRandomValue(checkList.length)],
          fortuneCount: sumCount + wishNum + businessNum * wish.length + healthBum * wish.length * business.length + 1,
        });

        return true;
      }

      sumCount += wish.length * business.length * health.length;
      return false;
    });
  };

  const resetFortune = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setOmikujiNumSwitching(false);

    setResult(defaultFortune);
  };

  const handleNumSwitching = () => {
    setOmikujiNumSwitching(!omikujiNumSwitching);
  };

  const OmikujiNum = () => {
    if (omikujiNumSwitching) {
      return <p>第{result.fortuneCount}番</p>;
    } else {
      return <p>第{numToKanji(result.fortuneCount)}番</p>;
    }
  };

  return (
    <>
      {result.fortune ? (
        <div className="grid text-sm md:text-base">
          <div
            className="mx-auto mb-7 cursor-pointer font-omikuji text-[26px] font-semibold tracking-[0.25rem]"
            onClick={handleNumSwitching}
          >
            <OmikujiNum />
          </div>

          <div className="mx-auto md:flex">
            <div className="">
              <Image src={result.image} width={150} height={300} alt="imege" className="inline" />
            </div>

            <div className="mt-7 border-4 border-red-600 p-2 font-omikuji [border-style:outset_double] [writing-mode:vertical-rl] md:ml-[72px] md:mt-0">
              <div className="flex">
                <div className="list-mark-vertical-icon px-[3px] py-0 font-semibold">願望：</div>
                <div className="mb-7 py-[2px] pr-[3px]">{result.detail.wish}</div>
              </div>

              <div className="flex">
                <div className="list-mark-vertical-icon px-[3px] py-0 font-semibold">商売：</div>
                <div className="mb-7 py-[2px] pr-[3px]">{result.detail.business}</div>
              </div>

              <div className="flex">
                <div className="list-mark-vertical-icon ml-0 px-[3px] py-0 font-semibold">健康：</div>
                <div className="mb-7 py-[2px] pr-[3px]">{result.detail.health}</div>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-7">
            <div className="list-mark-icon px-[3px] py-0 font-semibold">リリース前に、チェックしてみようっ！</div>
            <p className="mb-10 ml-5 w-72 break-normal px-0 py-[2px]">{result.checkList}</p>
          </div>

          <div className="relative grid justify-center">
            <button type="button" className="h-[90px] font-omikuji" onClick={resetFortune}>
              <Image src="/images/omikuji_retry.png" width={200} height={50} alt="retry" className="" />
              <div className="relative left-[6px] top-[-50px] inline-block font-semibold">もう一度引く？</div>
            </button>
          </div>
        </div>
      ) : (
        <div className="relative block h-[400px] w-full text-sm  md:text-base">
          <Image src={seasonsImage} width={450} height={450} alt="imege" className="mx-auto" />

          <button
            className="absolute left-[calc(50%-1rem)] top-[40%] z-40 mx-auto font-omikuji
            text-base font-bold md:top-[45%] md:text-xl"
            type="button"
            onClick={drawFortune}
          >
            <span className="whitespace-pre [writing-mode:vertical-rl]">おみくじを引く</span>
          </button>
        </div>
      )}
    </>
  );
};

export default OmikujiContents;
